/**
 * Drawing arrows and rings on a board: right-drag with a mouse or trackpad, or
 * two taps in draw mode on a touch screen.
 *
 * The hook listens on the wrapper around the board rather than on its squares,
 * and reads the square from the pointer's position against the wrapper's
 * bounding box. That is deliberate: the board is often `disabled` (pointer
 * events off), after a puzzle is solved and throughout draw mode, and then the
 * squares never see an event at all.
 *
 * Listeners are plain DOM listeners. React's synthetic events would arrive too
 * late to keep the press from the board: react-chessboard and chessboard-package
 * both run their own right-click arrows, and a press has to be stopped in the
 * capture phase, before it reaches them, for ours to be the only one drawn.
 */
import React, {useCallback, useEffect, useMemo, useReducer, useRef} from 'react';

import {boardArrows, boardRings, colourFor, Shape, squareAt, toggleShape} from './boardArrows';

type Orientation = 'white' | 'black';

export type UseBoardArrowsOptions = {
  boardSize: number;
  orientation: Orientation;
  /** The position shown; a new one clears the drawing (as lichess does after a move). */
  position: string;
  /** Taps draw instead of moving pieces: tap a square, tap another → arrow; tap the same square twice → ring. */
  drawMode: boolean;
};

export type BoardArrows = {
  /** Attach to the wrapper View around the board; on web it is the DOM element. */
  ref: React.MutableRefObject<any>;
  shapes: Shape[];
  /** Committed arrows, plus the in-progress preview arrow while right-dragging. */
  arrows: [string, string, string][];
  rings: {square: string; colour: string}[];
  /** Draw mode: the square tapped first, waiting for the second tap. */
  pendingFrom: string | null;
  clear: () => void;
};

type State = {
  /** The position the drawing belongs to. */
  position: string;
  shapes: Shape[];
  /** The arrow under a right-drag that has not been released yet. */
  preview: Shape | null;
  pendingFrom: string | null;
};

type Action =
  | {type: 'position'; position: string}
  | {type: 'clear'}
  | {type: 'preview'; shape: Shape | null}
  | {type: 'draw'; shape: Shape}
  | {type: 'tap'; square: string; colour: string}
  | {type: 'cancelPending'};

const NO_SHAPES: Shape[] = [];

const sameShape = (a: Shape | null, b: Shape | null) =>
  a === b || (!!a && !!b && a.from === b.from && a.to === b.to && a.colour === b.colour);

// Every no-op returns the same state object, so React skips the re-render: the
// preview is re-dispatched on every mousemove, and nearly all of them land on
// the square it is already showing.
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'position':
      return {position: action.position, shapes: NO_SHAPES, preview: null, pendingFrom: null};
    case 'clear':
      if (!state.shapes.length && !state.preview && !state.pendingFrom) return state;
      return {...state, shapes: NO_SHAPES, preview: null, pendingFrom: null};
    case 'preview':
      return sameShape(state.preview, action.shape) ? state : {...state, preview: action.shape};
    case 'draw':
      return {...state, preview: null, shapes: toggleShape(state.shapes, action.shape)};
    case 'tap':
      if (!state.pendingFrom) return {...state, pendingFrom: action.square};
      return {
        ...state,
        pendingFrom: null,
        shapes: toggleShape(state.shapes, {
          from: state.pendingFrom,
          to: action.square,
          colour: action.colour,
        }),
      };
    case 'cancelPending':
      return state.pendingFrom ? {...state, pendingFrom: null} : state;
    default:
      return state;
  }
}

/** The fields read off mouse and pointer events. */
type PointerLike = {
  button?: number;
  clientX: number;
  clientY: number;
  shiftKey?: boolean;
  altKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  pointerId?: number;
  pointerType?: string;
  preventDefault: () => void;
  stopPropagation: () => void;
};

type Latest = {boardSize: number; orientation: Orientation; drawMode: boolean};

// On native the ref is a host component with no DOM listeners, and under jest
// it is whatever the test put there; either way there is nothing to listen to.
const canListen = (target: any) =>
  !!target &&
  typeof target.addEventListener === 'function' &&
  typeof target.removeEventListener === 'function';

const colourOf = (e: PointerLike) =>
  colourFor({shift: e.shiftKey, alt: e.altKey, ctrl: e.ctrlKey, meta: e.metaKey});

/** Add the drawing listeners to the wrapper element; returns their removal. */
function listen(
  el: any,
  latest: React.MutableRefObject<Latest>,
  dispatch: React.Dispatch<Action>,
): () => void {
  // A right-drag is followed on the window, so it keeps tracking (and ends)
  // when the pointer leaves the board mid-drag.
  const host = (globalThis as any).window;
  const dragTarget = canListen(host) ? host : el;

  const squareOf = (e: PointerLike) => {
    const rect = el.getBoundingClientRect?.();
    if (!rect) return null;
    const {boardSize, orientation} = latest.current;
    return squareAt(e.clientX - rect.left, e.clientY - rect.top, boardSize, orientation);
  };

  let endDrag: (() => void) | null = null;

  const startDrag = (from: string) => {
    endDrag?.();
    dispatch({type: 'preview', shape: null});

    const onMove = (e: PointerLike) => {
      const to = squareOf(e);
      dispatch({
        type: 'preview',
        shape: to && to !== from ? {from, to, colour: colourOf(e)} : null,
      });
    };
    const onUp = (e: PointerLike) => {
      // A left click in the middle of a right-drag is not its end.
      if (e.button !== 2) return;
      stop();
      const to = squareOf(e);
      if (to) dispatch({type: 'draw', shape: {from, to, colour: colourOf(e)}});
      else dispatch({type: 'preview', shape: null});
    };
    const stop = () => {
      dragTarget.removeEventListener('mousemove', onMove);
      dragTarget.removeEventListener('mouseup', onUp);
      endDrag = null;
    };

    dragTarget.addEventListener('mousemove', onMove);
    dragTarget.addEventListener('mouseup', onUp);
    endDrag = stop;
  };

  const onContextMenu = (e: PointerLike) => e.preventDefault();

  const onMouseDown = (e: PointerLike) => {
    if (e.button === 2) {
      e.preventDefault();
      e.stopPropagation();
      const from = squareOf(e);
      if (from) startDrag(from);
      return;
    }
    // A plain click wipes the drawing, as on lichess. It must still reach the
    // board, or clicking a piece to move it would stop working. In draw mode a
    // click is a tap, handled below.
    if (e.button === 0 && !latest.current.drawMode) dispatch({type: 'clear'});
  };

  // Draw mode. A tap is a pointerdown and the pointerup of the same pointer;
  // the square is read on the way up. Stopping the press in the capture phase
  // keeps it from the pieces, and preventDefault on a pointerdown also stops
  // the browser sending the mousedown that would follow it.
  let tapPointer: number | undefined;
  let tapping = false;

  const onPointerDown = (e: PointerLike) => {
    if (!latest.current.drawMode) return;
    if (e.pointerType !== 'touch' && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    tapping = true;
    tapPointer = e.pointerId;
  };
  const onPointerUp = (e: PointerLike) => {
    if (!tapping || e.pointerId !== tapPointer) return;
    tapping = false;
    if (!latest.current.drawMode) return;
    e.stopPropagation();
    const square = squareOf(e);
    if (square) dispatch({type: 'tap', square, colour: colourOf(e)});
  };
  // The browser took the touch over (a scroll, a pinch): it was never a tap.
  const onPointerCancel = (e: PointerLike) => {
    if (e.pointerId === tapPointer) tapping = false;
  };

  el.addEventListener('contextmenu', onContextMenu);
  el.addEventListener('mousedown', onMouseDown, true);
  el.addEventListener('pointerdown', onPointerDown, true);
  el.addEventListener('pointerup', onPointerUp, true);
  el.addEventListener('pointercancel', onPointerCancel, true);

  return () => {
    // No dispatch here: this also runs on unmount.
    endDrag?.();
    el.removeEventListener('contextmenu', onContextMenu);
    el.removeEventListener('mousedown', onMouseDown, true);
    el.removeEventListener('pointerdown', onPointerDown, true);
    el.removeEventListener('pointerup', onPointerUp, true);
    el.removeEventListener('pointercancel', onPointerCancel, true);
  };
}

export function useBoardArrows({
  boardSize,
  orientation,
  position,
  drawMode,
}: UseBoardArrowsOptions): BoardArrows {
  const ref = useRef<any>(null);
  const [state, dispatch] = useReducer(reducer, position, (p: string) => ({
    position: p,
    shapes: NO_SHAPES,
    preview: null,
    pendingFrom: null,
  }));

  // Both resets happen during render, not in an effect, so the old drawing is
  // never painted over the new position for a frame (React re-runs this render
  // with the reset applied before committing anything).
  if (state.position !== position) dispatch({type: 'position', position});
  if (!drawMode && state.pendingFrom) dispatch({type: 'cancelPending'});

  // The listeners outlive renders; they read size, orientation and draw mode
  // from here at the moment of the event, so none of them re-registers.
  const latest = useRef<Latest>({boardSize, orientation, drawMode});
  useEffect(() => {
    latest.current = {boardSize, orientation, drawMode};
  });

  // Runs after every render but only acts when the wrapper element changed
  // (first mount, or the board being remounted under a new key), which a deps
  // array could not see: a ref's `current` is not a dependency React tracks.
  const attached = useRef<{el: any; detach: () => void} | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (attached.current && attached.current.el === el) return;
    attached.current?.detach();
    attached.current = null;
    if (!canListen(el)) return;
    attached.current = {el, detach: listen(el, latest, dispatch)};
  });
  useEffect(
    () => () => {
      attached.current?.detach();
      attached.current = null;
    },
    [],
  );

  const {shapes, preview, pendingFrom} = state;

  // Memoised so the array keeps its identity between renders: react-chessboard
  // re-syncs its arrows every time the `customArrows` prop changes identity.
  // react-chessboard also keys arrows by from-to, so a preview over an arrow
  // already drawn on the same squares stands in for it instead of doubling up.
  const arrows = useMemo(() => {
    const committed = boardArrows(shapes);
    if (!preview) return committed;
    const others = committed.filter(([f, t]) => !(f === preview.from && t === preview.to));
    return [...others, [preview.from, preview.to, preview.colour] as [string, string, string]];
  }, [shapes, preview]);
  const rings = useMemo(() => boardRings(shapes), [shapes]);
  const clear = useCallback(() => dispatch({type: 'clear'}), []);

  return {ref, shapes, arrows, rings, pendingFrom, clear};
}
